/** @genesys-custom-file — custom loading indicator (not from the shadcn registry). */

// @genesys-custom-file

import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'secondary' | 'muted';
  type?: 'dots' | 'circle';
  fullPage?: boolean;
}

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  (
    { className, size = 'sm', variant = 'default', type = 'circle', fullPage = false, ...props },
    ref,
  ) => {
    const sizeClasses = {
      sm: 'gap-1',
      md: 'gap-1.5',
      lg: 'gap-2',
    };

    const dotSizeClasses = {
      sm: 'h-1.5 w-1.5',
      md: 'h-2.5 w-2.5',
      lg: 'h-3 w-3',
    };

    const circleSizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-7 w-7',
      lg: 'h-8 w-8',
    };

    const dotVariantClasses = {
      default: 'bg-primary',
      primary: 'bg-primary',
      secondary: 'bg-secondary-foreground',
      muted: 'bg-muted-foreground',
    };

    const circleVariantClasses = {
      default: 'text-primary',
      primary: 'text-primary',
      secondary: 'text-secondary-foreground',
      muted: 'text-muted-foreground',
    };

    const dotsSpinner = (
      <div ref={ref} className={cn('flex items-center', sizeClasses[size], className)} {...props}>
        <div
          className={cn('animate-pulse rounded-full', dotSizeClasses[size], dotVariantClasses[variant])}
          style={{ animationDelay: '0ms', animationDuration: '1.4s' }}
        />
        <div
          className={cn('animate-pulse rounded-full', dotSizeClasses[size], dotVariantClasses[variant])}
          style={{ animationDelay: '160ms', animationDuration: '1.4s' }}
        />
        <div
          className={cn('animate-pulse rounded-full', dotSizeClasses[size], dotVariantClasses[variant])}
          style={{ animationDelay: '320ms', animationDuration: '1.4s' }}
        />
      </div>
    );

    const circleSpinner = (
      <div
        ref={ref}
        className={cn(
          'animate-spin rounded-full border-2',
          circleSizeClasses[size],
          circleVariantClasses[variant],
          'border-current/30 border-t-current',
          className,
        )}
        {...props}
      />
    );

    const spinner = type === 'circle' ? circleSpinner : dotsSpinner;

    if (fullPage) {
      return (
        <div className="top-[var(--titlebar-height)] fixed inset-0 z-50 flex items-center justify-center bg-background/80">
          {spinner}
        </div>
      );
    }

    return spinner;
  },
);

Spinner.displayName = 'Spinner';
